// Phase 4c — `(metric …)` tile op (kpi variant only).
//
// `(metric :variant kpi :span [W H] :value (col …) :label … :format …
//          :delta? (col …))`
//
// `:variant` is a single-member enum today (`kpi`); `gauge` and its
// `threshold` children are deferred. `:value` / `:delta` are explicit
// `(col …)` refs, evaluated lazily and stored unresolved (V-shape).
// Registered in `dashboardMeta` (NOT `opMeta`) — only reachable as a
// child of `(dashboard …)`.

import { z } from 'zod';

import { colRefArg, spanArg } from '../args.ts';
import { type DashboardCtx, dashboardMeta, resolveColKwarg } from '../dispatch.ts';
import { METRIC_VARIANTS, NUM_FORMATS } from '../enums.ts';
import type { MetricSpec } from '../value.ts';

export const metric = z
  .object({
    name: z.literal('metric'),
    params: z.tuple([]),
    variant: z.enum(METRIC_VARIANTS).default('kpi'),
    span: spanArg,
    value: colRefArg,
    label: z.string().min(1),
    format: z.enum(NUM_FORMATS),
    delta: colRefArg.optional(),
  })
  .describe(
    'A single-number KPI tile. `:value` is an explicit `(col ds colname)`; `:format` controls rendering; ' +
      'optional `:delta` (col …) drives a change indicator.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(metric :variant kpi :span [4 2] :value (col wells total) :label "Total" :format compact)' },
    apply: async (data, ctx: DashboardCtx): Promise<MetricSpec> => ({
      kind: 'metric-spec',
      variant: data.variant,
      span: data.span,
      value: await resolveColKwarg(data.value, ctx, 'value'),
      label: data.label,
      format: data.format,
      ...(data.delta === undefined ? {} : { delta: await resolveColKwarg(data.delta, ctx, 'delta') }),
    }),
  });
